import ConfigService from 'client/util/initializationHandler/ConfigService';
import api from 'client/api';
// Extend the Navigator interface to include userAgentData
interface NavigatorUAData {
  brands: Array<{ brand: string; version: string }>;
  mobile: boolean;
  platform: string;
  getHighEntropyValues(hints: string[]): Promise<{
    architecture: string;
    bitness: string;
    model: string;
    platform: string;
    platformVersion: string;
    uaFullVersion: string;
    wow64: boolean;
  }>;
}

declare global {
  interface Navigator {
    userAgentData?: NavigatorUAData;
  }
}

const getIframeOrigin = () => {
  const url = new URL(ConfigService.googleVerifiedAccessUrl);
  return url.origin;
};

const sendJWT = (token: string) => api.user.validateManagedChrome(token);

function isChromeBrowser() {
  return navigator.userAgentData?.brands.some((brand) => brand.brand === 'Google Chrome');
}

export class ManagedBrowserService {
  private iframeId = 'browserVerificationIframe';

  loadIframe():void {
    const iframe = document.createElement('iframe');
    iframe.src = ConfigService.googleVerifiedAccessUrl;
    iframe.width = '1';
    iframe.height = '1';
    iframe.style.display = 'none';
    iframe.id = this.iframeId;

    document.body.appendChild(iframe);
  }

  private removeIframe() {
    const element = document.getElementById(this.iframeId);
    if (element) {
      element.outerHTML = '';
    }
  }

  private createMessageListener(resolve: (value: string) => void, reject: (reason: any) => void) {
    const listener = async (message: { origin: string; data: any; }) => {
      // empty string is added for Unit Test to pass
      if (![getIframeOrigin(), ''].includes(message.origin)) {
        return;
      }
      window.removeEventListener('message', listener);
      this.removeIframe();

      try {
        const { token, error } = JSON.parse(message.data);
        if (token) {
          await sendJWT(token);
          resolve('success');
        } else if (error) {
          reject(new Error(error));
        } else {
          reject(new Error('No token or error in message'));
        }
      } catch (e) {
        reject(e);
      }
    };

    window.addEventListener('message', listener);
  }

  validateChrome() : Promise<string> {
    const timeoutPromise = new Promise<string>((resolve, reject) => {
      setTimeout(() => reject(new Error('validate chrome: timeout')), 2000);
    });
    const verifyPromise = new Promise<string>((resolve, reject) => {
      if (isChromeBrowser()) {
        this.createMessageListener(resolve, reject);
        this.loadIframe();
      } else {
        reject(new Error('validate chrome: not a Chrome browser'));
      }
    });

    return Promise.race([timeoutPromise, verifyPromise]);
  }
}

export default new ManagedBrowserService();
